import { useEffect } from 'react';
import { useTour } from '@reactour/tour';
import { useQueryParam } from 'use-query-params';
import { StringQueryParam } from 'root/src/client/v2/queryParams/helpers/params.helper';
import { RedesignOnboardingStep } from 'root/src/client/v2/onboarding/redesign/RedesignOnboardingStep';
const OnboardingCopy = ({ title, description }) => (<div>
		<p className="font-display text-base font-semibold text-foreground">{title}</p>
		<p className="mt-1 text-sm text-muted-foreground">{description}</p>
	</div>);
// Redesign-only steps for the /nova/drops list tour. Anchored on the redesign tabs / filters / grid.
export const REDESIGN_DROPS_TOUR_STEPS = [
    {
        selector: '[data-onboarding="drops-tabs"]',
        position: 'bottom',
        content: (<RedesignOnboardingStep>
				<OnboardingCopy title="Browse by status" description="Switch between Live, Upcoming and Ended drops to see what's on."/>
			</RedesignOnboardingStep>),
    },
    {
        selector: '[data-onboarding="drops-filters"]',
        position: 'bottom',
        content: (<RedesignOnboardingStep>
				<OnboardingCopy title="Filter drops" description="Narrow down by game, genre or platform to find drops that fit your channel."/>
			</RedesignOnboardingStep>),
    },
    {
        selector: '[data-onboarding="drops-grid"]',
        position: 'top',
        content: (<RedesignOnboardingStep>
				<OnboardingCopy title="Pick a drop" description="Open a drop to see its games, accept a dare, and earn rewards for you and your viewers."/>
			</RedesignOnboardingStep>),
    },
];
// Redesign-only controller for the drops list tour. Opens the tour when the page is visited with the
// redesign-specific `?tour=drops` param (the shared `useDropsOnboarding` reads `onboarding`, so it never
// double-fires here and the legacy /drops page is untouched). Rendered inside RedesignDropsPage's
// TourProvider. Waits for the drops grid anchor to exist before opening: until the drops load, the step
// selectors match nothing and reactour parks the popovers in the top-left corner. Falls back to opening
// anyway after a short wait so it never hangs (e.g. an empty drops list).
export const RedesignDropsOnboarding = () => {
    const [tourParam] = useQueryParam('tour', StringQueryParam());
    const tour = useTour();
    useEffect(() => {
        if (tourParam !== 'drops') {
            return undefined;
        }
        let cancelled = false;
        let attempts = 0;
        const open = () => {
            var _a;
            (_a = tour.setSteps) === null || _a === void 0 ? void 0 : _a.call(tour, REDESIGN_DROPS_TOUR_STEPS);
            tour.setIsOpen(true);
        };
        const waitForAnchor = () => {
            if (cancelled) {
                return;
            }
            if (document.querySelector('[data-onboarding="drops-grid"]') || attempts > 60) {
                open();
                return;
            }
            attempts += 1;
            window.setTimeout(waitForAnchor, 150);
        };
        waitForAnchor();
        return () => {
            cancelled = true;
        };
    }, [tourParam]);
    return null;
};
